import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { X, ArrowLeft } from 'lucide-react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

/**
 * Global fullscreen image viewer (lightbox) — Telegram/WhatsApp style.
 * Features:
 *  - Top-right ✕ close, top-left ← back
 *  - Click on backdrop to close (image click doesn't propagate)
 *  - Android hardware Back / iOS back-gesture support via history pushState + popstate
 *  - ESC key closes on desktop
 *  - Vertical swipe-down (or up) to dismiss, with background fade + spring return if not enough
 *  - Pinch-to-zoom + double-tap-to-zoom + pan when zoomed
 *  - Swipe-to-close is disabled while image is zoomed (so you can pan freely)
 *  - Body scroll lock while open (preserves user's scroll position on close)
 */

const LightboxContext = createContext(null);

export function useLightbox() {
  const ctx = useContext(LightboxContext);
  if (!ctx) throw new Error('useLightbox must be used within LightboxProvider');
  return ctx;
}

const SWIPE_CLOSE_THRESHOLD = 110; // px vertical distance to trigger close
const SWIPE_VELOCITY_THRESHOLD = 500; // px/sec

export function LightboxProvider({ children }) {
  const [state, setState] = useState({ open: false, src: null, alt: '' });
  const pushedRef = useRef(false);
  const scrollYRef = useRef(0);

  const open = useCallback((src, alt = '') => {
    if (!src) return;
    scrollYRef.current = window.scrollY;
    try {
      window.history.pushState({ lightboxOpen: true }, '');
      pushedRef.current = true;
    } catch { pushedRef.current = false; }
    setState({ open: true, src, alt });
  }, []);

  const close = useCallback(({ fromPopState = false } = {}) => {
    setState({ open: false, src: null, alt: '' });
    if (!fromPopState && pushedRef.current) {
      pushedRef.current = false;
      try { window.history.back(); } catch { /* noop */ }
    } else {
      pushedRef.current = false;
    }
  }, []);

  // Hardware Back (Android) / iOS back-gesture → close lightbox
  useEffect(() => {
    const onPop = () => {
      setState((s) => {
        if (s.open) {
          pushedRef.current = false;
          return { open: false, src: null, alt: '' };
        }
        return s;
      });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Lock body scroll while open
  useEffect(() => {
    if (!state.open) return;
    const y = scrollYRef.current;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      window.scrollTo(0, y);
    };
  }, [state.open]);

  // ESC closes on desktop
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.open, close]);

  return (
    <LightboxContext.Provider value={{ open, close, isOpen: state.open }}>
      {children}
      {createPortal(
        <AnimatePresence>
          {state.open && (
            <LightboxOverlay
              key="lightbox-overlay"
              src={state.src}
              alt={state.alt}
              onClose={() => close()}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </LightboxContext.Provider>
  );
}

function LightboxOverlay({ src, alt, onClose }) {
  const [zoomed, setZoomed] = useState(false);
  const y = useMotionValue(0);
  // Fade the backdrop as user drags the image away
  const backdropOpacity = useTransform(y, [-260, 0, 260], [0.15, 1, 0.15]);

  const handleDragEnd = (_, info) => {
    if (zoomed) return; // don't allow swipe-to-close while zoomed
    const dy = Math.abs(info.offset.y);
    const vy = Math.abs(info.velocity.y);
    if (dy > SWIPE_CLOSE_THRESHOLD || vy > SWIPE_VELOCITY_THRESHOLD) {
      onClose();
    } else {
      // spring back is automatic via dragSnapToOrigin
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center safe-top safe-bottom"
      role="dialog"
      aria-modal="true"
      data-testid="lightbox-overlay"
    >
      {/* Backdrop (its opacity animates with drag) */}
      <motion.div
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
        style={{ opacity: backdropOpacity }}
        onClick={onClose}
      />

      {/* Top-left back button */}
      <button
        data-testid="lightbox-back-btn"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Indietro"
        className="absolute z-20 w-11 h-11 rounded-full bg-black/50 border border-white/15 backdrop-blur flex items-center justify-center text-white/90 hover:text-[var(--gold)] hover:border-[var(--gold)]/40 transition-colors"
        style={{ top: 'max(1rem, env(safe-area-inset-top))', left: 'max(1rem, env(safe-area-inset-left))' }}
      >
        <ArrowLeft className="w-5 h-5" />
      </button>

      {/* Top-right close button */}
      <button
        data-testid="lightbox-close-btn"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Chiudi"
        className="absolute z-20 w-11 h-11 rounded-full bg-black/50 border border-white/15 backdrop-blur flex items-center justify-center text-white/90 hover:text-[var(--gold)] hover:border-[var(--gold)]/40 transition-colors"
        style={{ top: 'max(1rem, env(safe-area-inset-top))', right: 'max(1rem, env(safe-area-inset-right))' }}
      >
        <X className="w-5 h-5" />
      </button>

      {/* Draggable + zoomable image container */}
      <motion.div
        className="relative z-10 flex items-center justify-center w-full h-full"
        drag={zoomed ? false : 'y'}
        dragElastic={0.6}
        dragSnapToOrigin
        style={{ y }}
        onDragEnd={handleDragEnd}
        onClick={(e) => {
          // Click outside image (on padding area) closes only if not zoomed
          if (!zoomed && e.target === e.currentTarget) onClose();
        }}
      >
        <TransformWrapper
          minScale={1}
          maxScale={5}
          initialScale={1}
          doubleClick={{ mode: 'toggle', step: 1.6 }}
          wheel={{ step: 0.2 }}
          pinch={{ step: 5 }}
          panning={{ disabled: false, velocityDisabled: true }}
          limitToBounds
          centerOnInit
          onZoomStop={(ref) => setZoomed(ref.state.scale > 1.02)}
          onPinchingStop={(ref) => setZoomed(ref.state.scale > 1.02)}
        >
          <ZoomInner src={src} alt={alt} />
        </TransformWrapper>
      </motion.div>
    </motion.div>
  );
}

// Inner content of TransformWrapper — has access to controls if we ever need them
function ZoomInner({ src, alt }) {
  return (
    <TransformComponent
      wrapperStyle={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      contentStyle={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <motion.img
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        src={src}
        alt={alt || ''}
        onClick={(e) => e.stopPropagation()}
        draggable={false}
        className="max-w-[92vw] max-h-[86vh] object-contain rounded-lg select-none"
        data-testid="lightbox-image"
      />
    </TransformComponent>
  );
}
